import {useState} from 'react'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import TermsConditionsPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/TermsConditionsPopup.jsx'
import {logoLatest} from '@img/index.js'
import ConfirmBookingReceivedTabs from '@/customComponents/editRideComponents/PageModals/ComponentPopups/ConfirmBookingReceivedTabs.jsx'

/**
 * Shown after a successful submit on the confirm (booking creation) and edit pages.
 *
 * `isEdit` switches it to the EDIT wording: the trip already has a vehicle and driver
 * being sourced, so "we're now finding the perfect vehicle and a professional driver"
 * is only true when the booking is first created/confirmed — never after a change.
 */
export default function SuccessPopup({isOpen, isReturnFlow, isEdit, onDone, trip}) {
    const [showTerms, setShowTerms] = useState(false)

    // Creating a booking → the full booking-received CARD DECK, for EVERY confirm-page
    // route into it: return booking, rebook, or a plain confirm. It used to require
    // isReturnFlow, so a rebook fell through to the old plain "Booking Received !" panel
    // and the confirm page had two different success screens depending on how you got
    // there. The deck's own copy switches on trip_type, so it reads correctly either way.
    //
    // EDIT keeps the simple confirmation: nothing was created, so a deck selling perks
    // and next steps would be the wrong screen. `trip` is still required — the deck is
    // built entirely from it.
    const tabbed = !isEdit && !!trip

    const title = isEdit
        ? `${isReturnFlow ? 'Return ' : ''}Booking Edit Information Received !`
        : (isReturnFlow ? 'Return Booking Received !' : 'Booking Received !')

    return (
        <>
            {/* The deck brings its own card surfaces + .modalPages-spacing, so the panel is
                stripped (.hopn-br-modal) and the body left with NO padding at all —
                flushX drops the side gutters (including the mobile px-4), flushTop the
                top gap. The slides are meant to span the full modal width; any padding
                here shrinks the deck and breaks the peeked-card offsets, which are a
                percentage of the slide. Matches what step-bookingReceived does for the
                booking modal. */}
            <Modal isOpen={isOpen} onClose={() => {}} centered={tabbed} flushBody={tabbed} flushX={tabbed} flushTop={tabbed}
                   className={tabbed ? 'hopn-br-modal' : 'md:max-w-137.5'}>
                {tabbed ? (
                    // No terms link on the deck — the greeting card carries the greeting only.
                    // The simple confirmation below still has one, so TermsConditionsPopup stays.
                    <ConfirmBookingReceivedTabs trip={trip} onDone={onDone}/>
                ) : (
                    <div className="flex flex-col items-center py-6 text-center">
                        <span className="bg-(--accent-green-light) rounded-full px-10 py-3 mb-6 inline-flex items-center justify-center">
                            <img src={logoLatest} alt="Hopn" className="h-7"/>
                        </span>

                        <h3 className="font-bold mb-6!">{title}</h3>

                        <div className="ls-spinner mb-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                                <span key={i} className={`ls-dot ls-dot--${i}`}/>
                            ))}
                        </div>

                        {!isEdit && (
                            <p className="text-(--text-secondary) mb-2">We're now finding the perfect vehicle and a professional driver for your trip.</p>
                        )}
                        <p className="text-(--text-secondary) mb-6">Hang tight — your final confirmation and trip details will be on the way shortly!</p>

                        <button type="button" onClick={() => setShowTerms(true)} className="mb-6 text-sm cursor-pointer underline bg-transparent border-0 text-(--text-primary)">
                            By proceeding, you confirm that you understand and agree to these terms.
                        </button>

                        <button type="button" onClick={onDone}
                                className="cursor-pointer w-full py-[15px] rounded-full bg-(--text-primary) text-white font-bold text-base flex items-center justify-center min-h-[55px] border-0">
                            Done
                        </button>
                    </div>
                )}
            </Modal>

            <TermsConditionsPopup isOpen={showTerms} onClose={() => setShowTerms(false)}/>
        </>
    )
}
